import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Asks before leaving the page with unsaved edits. The app uses <BrowserRouter> (no data
 * router, so no useBlocker): in-app link clicks (sidebar, breadcrumbs…) are caught in the
 * capture phase, the page's own buttons use `guardedNavigate`, and reloads / closing the
 * tab get the browser's beforeunload prompt. The browser Back button isn't intercepted.
 */
export default function useUnsavedGuard(dirty) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    const onClick = (e) => {
      if (!dirtyRef.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const path = `${url.pathname}${url.search}${url.hash}`;
      if (path === `${window.location.pathname}${window.location.search}`) return;
      e.preventDefault();
      e.stopPropagation();
      setPending(path);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  const guardedNavigate = useCallback(
    (to) => {
      if (dirtyRef.current) setPending(to);
      else navigate(to);
    },
    [navigate],
  );

  return {
    pending,
    guardedNavigate,
    confirm: () => {
      const to = pending;
      setPending(null);
      dirtyRef.current = false;
      navigate(to);
    },
    cancel: () => setPending(null),
  };
}
